import type { CodeSessionPosture } from '@/generated/prisma/enums';
import type { CodingAgentRebaseSession } from '@/lib/ai/coding-agent-service/coding-agent-writeback/git-rebase';
import type { CodingAgentCredentialFingerprint } from '@/lib/ai/coding-agent-service/credential-fingerprint';
import type { CodeSessionHarnessProfile } from '@/lib/ai/coding-agent-service/harnesses/opencode/config';
import type { CodeSessionRedactionInput } from '@/lib/code-sessions/types';
import type {
  WorkspaceChangesResult,
  WorkspaceDiffResult,
  WorkspaceFileResult,
} from '@/lib/code-sessions/workspace-types';

import type { BotTurnPublication } from './bot-publication';
import type { BotTurnPrepOutput } from './bot-turn-prep-script';
import type { OpenCodeClient } from './opencode-http-client';
import type { SessionNetworkState } from './session-network';

export const CODE_SANDBOX_STATUS = {
  PENDING: 'pending',
  SPAWNING: 'spawning',
  CONNECTING: 'connecting',
  READY: 'ready',
  SNAPSHOTTING: 'snapshotting',
  STOPPED: 'stopped',
  FAILED: 'failed',
  STALE: 'stale',
} as const;
export type CodeSandboxStatus = (typeof CODE_SANDBOX_STATUS)[keyof typeof CODE_SANDBOX_STATUS];

export const CODE_BOOT_PHASE = {
  /** Forks only: stopping the parent writes the snapshot the fork's sandbox boots from. */
  SNAPSHOT: 'snapshot',
  CREATE: 'create',
  CHECKOUT: 'checkout',
  /** New sessions only: the repository environment's setup script. Forks inherit its result. */
  SETUP: 'setup',
  HARNESS: 'harness',
  READY: 'ready',
} as const;
export type CodeBootPhase = (typeof CODE_BOOT_PHASE)[keyof typeof CODE_BOOT_PHASE];

export type CodeBootProgress = {
  phase: CodeBootPhase;
  /**
   * `progress` updates a running phase's detail and output without restarting
   * it; `skipped` and `warning` settle a phase without failing the boot.
   */
  status: 'started' | 'progress' | 'completed' | 'skipped' | 'warning' | 'failed';
  elapsedMs?: number;
  detail?: string;
  /** The setup script's last output lines, oldest first. */
  output?: string[];
  /**
   * Marks a setup row running a script cubic wrote, because the repository
   * has none saved. Optional: events stored before it existed lack it.
   */
  detected?: true;
};

/**
 * How a setup script run ended. `output` holds its last lines, cleaned of
 * terminal controls and redacted. A `dirty` run exited 0 but left the checkout
 * changed, with uncommitted changes or a moved HEAD, which no session accepts
 * from a snapshot; its `output` lists the changes.
 */
export type CodeSetupOutcome =
  | { status: 'succeeded'; durationMs: number; output: string[] }
  | { status: 'failed'; exitCode: number; durationMs: number; output: string[] }
  | { status: 'timed_out'; durationMs: number; output: string[] }
  | { status: 'dirty'; durationMs: number; output: string[] };
/** A script run's result, before snapshot preparation checks the checkout. */
export type CodeSetupRunOutcome = Exclude<CodeSetupOutcome, { status: 'dirty' }>;
export type CodeSetupSuccess = Extract<CodeSetupOutcome, { status: 'succeeded' }>;
export type CodeSetupFailure = Exclude<CodeSetupOutcome, CodeSetupSuccess>;

/**
 * What a session's VM got from the setup script, which its agent is told: how
 * its run ended, that the repository has none, or that it could not run or be
 * followed. `running` is a script the boot left running in the background
 * (`CodeSessionAccessContext.setupInBackground`), which has no outcome yet.
 */
export type CodeSessionSetupState = (
  | { status: 'no_script' | 'could_not_run' | 'timed_out' | 'running' }
  | {
      status: 'succeeded';
      /**
       * The earlier commit whose snapshot's run this VM reuses, since no file that decides what
       * setup installs changed after it (`runSetupScript`): the script did not run on this commit.
       */
      reusedFrom?: string;
    }
  | { status: 'failed'; exitCode: number }
) & {
  /** The script was detected from the checkout, as the repository has none saved. */
  detected: boolean;
};

/**
 * What a session VM's firewall allows, which its agent is told: the session's
 * mode after the restriction floor, its repository's (they differ only for a
 * session restricted before its repository opened again), and the registry
 * and custom hosts, without the default, MCP and credential hosts the
 * firewall also allows.
 */
export type SessionNetworkView = Pick<
  SessionNetworkState,
  'mode' | 'repositoryMode' | 'allowedHosts'
>;

/** Output lines a running setup script streams to the setup card, which is sized for them. */
export const CODE_SETUP_LIVE_OUTPUT_LINES = 4;

/**
 * Variables cubic sets for a setup script, beside the repository's own, which
 * cannot shadow them: the `CUBIC_` prefix is reserved.
 */
export const CODE_SETUP_SCRIPT_VARIABLES = {
  /** `snapshot` while building the cached environment, `session` in a session's own sandbox. */
  mode: 'CUBIC_SETUP_MODE',
  /** The repository root, where the script runs. */
  workspacePath: 'CUBIC_WORKSPACE_PATH',
  /** The commit the script sets up. */
  commitSha: 'CUBIC_COMMIT_SHA',
  /**
   * The commit this checkout was last set up at by the same script, when that
   * run's files are still in place: the seed's commit on a VM restored from an
   * older snapshot. Unset on a fresh checkout or when running again on the same
   * commit. Its objects are in the checkout, so e.g.
   * `git diff --quiet "$CUBIC_PREVIOUS_COMMIT_SHA" HEAD -- pnpm-lock.yaml` works.
   */
  previousCommitSha: 'CUBIC_PREVIOUS_COMMIT_SHA',
} as const;

/**
 * A session VM's vCPUs; Vercel allocates 2 GB per vCPU, so 16 GB for
 * repository type checks alongside OpenCode. A setup script builds with the same.
 */
export const CODE_SESSION_VCPUS = 8;

/** Repo identity needed to mint the brokered GitHub publish token. */
export type CodeSessionRepoContext = {
  installationId: number;
  repositoryId: number;
  repoFullName: string;
};

/**
 * A session VM's repository and whose MCP servers it runs: always the
 * session's creator's, whoever sends the turn (a fork's is whoever forked it).
 * The firewall holds the creator's own accounts' tokens only on their turns.
 */
export type CodeSessionAccessContext = CodeSessionRepoContext & {
  createdByProfileId: string;
  /**
   * What every launch of the VM's front door runs OpenCode with, from who owns
   * the session (`harnessProfileOf`): a session cubic owns runs `bot_session`,
   * with no MCP servers, whoever's turn or read wakes it.
   */
  harnessProfile: CodeSessionHarnessProfile;
  /**
   * Whose code the VM runs (`session-posture.ts`): a fork's, in the session cubic owns on a
   * pull request from a fork, gets no GitHub token, repository environment, setup script or push
   * grant. Every context `accessContextOf` builds carries it; absent, it is the repository's own.
   */
  posture?: CodeSessionPosture;
  /**
   * A setup-only session (`isSetupOnlySession`): its VM gets a GitHub token that only reads, and
   * its front door offers only the environment tools and the environment setup prompt.
   */
  setupOnly?: true;
  /**
   * A pull request page's chat session (source `pr_page`): its front door also offers the page's
   * tools (`session-tools/pr-page.ts`).
   */
  prPage?: true;
  /**
   * A session whose boot need not wait for its setup script (`setupRunsInBackground`; see "Setup in
   * the background" in the code-sessions README).
   */
  setupInBackground?: true;
};

/** What a running session sandbox needs from its session: repo identity, its creator and the model it prompts with. */
export type CodeSessionRuntimeContext = CodeSessionAccessContext & {
  /**
   * The session's stored model, e.g. `vercel/anthropic/claude-sonnet-5` or
   * `ai-subscription/chatgpt/gpt-6.1-sol` (`resolveCodeSessionModel`).
   */
  model: string;
  /**
   * Whether the firewall may hold the tokens of the creator's own MCP
   * accounts (`creatorAccountsFor` decides). Without them, the VM runs the
   * same servers, with only the API keys.
   */
  creatorAccounts: boolean;
};

/** Non-secret provider scope used to isolate reusable repository snapshots. */
export type RepositorySnapshotNamespace = {
  environment: string;
  teamId: string;
  projectId: string;
};

/**
 * Where a new session's checkout starts. `snapshot` and `seed` both restore the
 * scope's latest snapshot, the last checkout prepared for the same repository,
 * branch and image. It is a `snapshot` when it holds the exact commit, and a
 * `seed` when it holds another, which the boot fetches the commit into.
 * `image` clones into the image. A snapshot is only published once its setup
 * succeeded.
 */
export type RepositorySource =
  | {
      kind: 'snapshot';
      snapshotId: string;
      scopeHash: string;
      commitSha: string;
    }
  | {
      kind: 'seed';
      snapshotId: string;
      scopeHash: string;
      seedCommitSha: string;
      commitSha: string;
    }
  | {
      kind: 'image';
      commitSha: string;
      reason: string;
    };

/** A repository's setup script and the hash its cached result is keyed by. */
export type CodeSessionSetupScript = { script: string; hash: string };

export type CodeSandboxHandle = {
  sandboxName: string;
  /** Base URL of the agent front door, which the session controller must be able to reach. */
  baseUrl: string;
};

/**
 * Sandbox operations used by cloud Code session workflows. Implementations own
 * allocation, lifecycle, agent readiness and provider-specific credential/network
 * handling; callers retain session access checks and turn orchestration.
 * A new implementation must preserve the operation contracts below.
 */
export type CodeSandboxDriver = {
  create: (input: {
    sessionId: string;
    sandboxName: string;
    repoUrl: string;
    /** The checkpointed commit SHA, never a moving branch reference. */
    revision: string;
    imageIdentity: string;
    /** Where the checkout starts; its `commitSha` is `revision`. */
    source: RepositorySource;
    sessionBranch: string;
    /**
     * Repo identity for the brokered publish token minted inside the driver,
     * and the session's creator, whose own accounts the boot brokers: no
     * prompt reaches OpenCode before a turn's configuration decides.
     */
    repo: CodeSessionAccessContext;
    /**
     * The session's stored model, e.g. `vercel/anthropic/claude-sonnet-5` or
     * `ai-subscription/chatgpt/gpt-6.1-sol` (`resolveCodeSessionModel`).
     */
    model: string;
    /** The repository environment's plain variables, for setup and every command the agent runs. */
    variables: Record<string, string>;
    /** The names in `variables` saved as secrets, whose values setup output must not show. */
    secretVariableNames: readonly string[];
    /**
     * What setup runs with (`setupContextHash`), hashed with any script cubic writes when none is
     * saved. Unread on a fork's pull request, whose boot runs no script.
     */
    setupContext: string;
    /** The repository environment's setup script; null when it has none. */
    setup: CodeSessionSetupScript | null;
    onBoot?: (progress: CodeBootProgress) => void | Promise<void>;
  }) => Promise<
    CodeSandboxHandle & {
      setup: CodeSessionSetupState;
      /**
       * For a setup script the boot left running in the background (`setup` is `running`), whose
       * row reports to `onBoot` past the boot's ready: what the VM got from it once it ended and
       * the front door it may have stopped serves again. Never rejects.
       */
      setupSettled?: Promise<CodeSessionSetupState>;
    }
  >;
  /**
   * Boot a spare sandbox (`quick-chat/`): a sandbox from the image with OpenCode running and no
   * checkout, whose plugin's gate holds the agent's workspace tools (`buildWorkspaceHoldCommand`).
   * Its name makes its access a spare's: no repository credentials, GitHub token or repository
   * hosts.
   */
  bootSpare: (input: {
    spareId: string;
    sandboxName: string;
    /** The context of the sessions that may claim it; its own access drops the repository's part. */
    repo: CodeSessionAccessContext;
    model: string;
    imageIdentity: string;
  }) => Promise<CodeSandboxHandle>;
  /**
   * Release a spare's gate (`buildWorkspaceHoldCommand`), so the workspace tools it held run, in the
   * spare, on no checkout: once its session's own sandbox failed, so no call waits on it.
   */
  releaseSpareWorkspace: (sandboxName: string) => Promise<void>;
  /**
   * Copy the parent's VM from its latest snapshot, taken when it last stopped,
   * into a new session sandbox: the same files, uncommitted work, and OpenCode
   * database, on the fork's own branch, environment, credentials, and tags,
   * with OpenCode running. Idempotent by `sandboxName`.
   */
  fork: (input: {
    sessionId: string;
    sandboxName: string;
    sessionBranch: string;
    parent: { sessionId: string; sandboxName: string; sessionBranch: string };
    /** The fork's own creator: whoever forked it, whose own accounts it boots with, as `create` does. */
    repo: CodeSessionAccessContext;
    model: string;
    /** The repository environment's current variables; the fork's boot writes them over the parent's. */
    variables: Record<string, string>;
    /** The names in `variables` saved as secrets, whose values the boot fingerprints. */
    secretVariableNames: readonly string[];
    onBoot?: (progress: CodeBootProgress) => void | Promise<void>;
  }) => Promise<CodeSandboxHandle>;
  /**
   * Delete the sandbox; a sandbox that no longer exists is not an error. Its snapshots stay until
   * they expire on their own unless `deleteSnapshots` asks for the ones no other sandbox uses to
   * go with it: a session whose code must not outlive it asks.
   */
  delete: (sandboxName: string, options?: { deleteSnapshots?: boolean }) => Promise<void>;
  /**
   * Return a running sandbox with a healthy OpenCode server that lists the
   * session's model, and an extended idle timeout. Refresh network
   * credentials, including a repository-scoped GitHub token, the
   * repository's own credentials and the creator's MCP servers', their own
   * accounts' only with `session.creatorAccounts`. Resume the VM or restart
   * OpenCode when needed; an OpenCode started here connects the creator's MCP
   * servers before this returns. One already running keeps the servers it
   * started with until it runs nothing: then one started with other servers
   * than this read's is started again. A VM configured in the last 10 minutes,
   * for the same `creatorAccounts`, under open access, that is still the same
   * running VM with a ready front door keeps that configuration while the
   * OAuth tokens it brokers stay fresh (`SessionAccess.reusableUntil`); one
   * under restricted access, or whose repository's access was restricted
   * since, is configured again, and one on an AI subscription never keeps a
   * configuration, since each brokers it a token fresh enough for the run.
   * A VM the provider no longer has, or can no longer resume, throws
   * `CodeSessionSandboxLostError`.
   */
  ensureRunning: (
    sandboxName: string,
    session: CodeSessionRuntimeContext,
    onBoot?: (progress: CodeBootProgress) => void | Promise<void>,
  ) => Promise<
    CodeSandboxHandle & {
      /**
       * On a VM whose AI subscription claim holds a token, when the token this configuration
       * brokered opens its refresh window (epoch milliseconds), for the turn run it starts.
       */
      aiSubscriptionRefreshAt?: number;
    }
  >;
  /** Extend a running VM's idle timeout without restarting a stopped VM. */
  touch: (sandboxName: string) => Promise<void>;
  /**
   * A session cubic owns, before its next turn is admitted, in a VM `ensureRunning` just
   * configured: revoke any publication grant an earlier turn left, kill every process an earlier
   * turn left and reset the repository's Git state while the firewall brokers no GitHub token
   * and forwards GitHub to no grant (`BOT_TURN_RESET_SCRIPT`), then reset the workspace to
   * `headBranch`'s current tip (`BOT_TURN_SYNC_SCRIPT`), fetched with a token that can only read
   * the repository's contents and is revoked after. Then writes `files` (a thread context too
   * long for the prompt). Resolves the head it reset to. The VM's firewall brokers no working
   * GitHub token until `relaunchBotFrontDoor` restores the session's own access. In a session on
   * a fork's pull request (`posture`), the workspace is reset to the base repository's
   * `refs/pull/<pullNumber>/head` instead, never fetched from the fork, and the firewall brokers
   * no GitHub token at all once that fetch ends.
   */
  prepareBotWorkspace: (
    sandboxName: string,
    session: CodeSessionRuntimeContext,
    input: {
      /** The session's control lease; once lost, stop before the next write and abandon the phase in flight. */
      signal: AbortSignal;
      headBranch: string;
      /** The pull request the session works on; a fork's session cannot reset without it. */
      pullNumber?: number | null;
      files: readonly { path: string; content: string }[];
      /** Commits of the head's history to fetch; absent fetches the head alone. */
      historyDepth?: number;
    },
  ) => Promise<BotTurnPrepOutput>;
  /**
   * Grant the VM's current session the turn's `publication` (a GitHub proxy grant scoped to the
   * pull request's head branch, `bot-publication.ts`), restore the session's own read-only
   * access on the VM's firewall with GitHub's hosts forwarded to that grant, start the push
   * relay, and write the turn's Git config (committer, author, relay, signing); then relaunch
   * the front door with the live turn's settings until OpenCode is ready and lists the session's
   * model. A failure revokes the grant. Resolves the attempt the cubic plugin stamped into its
   * load marker, or null when it wrote none in time. A turn of a run's own session, which pushes
   * nothing, gets a read-only `publication` (`botTurnReadOnlyGrantPermissions`): the firewall
   * forwards GitHub's hosts to a grant that refuses every push, and Git gets no push relay or
   * signing. With no `publication`, for any other turn that pushes nothing (`botTurnNoPushReason`:
   * a fork's, or one whose run opens a new pull request), nothing is granted: the firewall
   * forwards GitHub nowhere, the session's own token only reads, and Git commits as cubic with no
   * push relay. A session on a fork's pull request is never granted one: a `publication` for it
   * throws before anything is granted.
   */
  relaunchBotFrontDoor: (
    sandboxName: string,
    session: CodeSessionRuntimeContext,
    publication: BotTurnPublication | null,
  ) => Promise<{
    handle: CodeSandboxHandle;
    pluginAttempt: string | null;
    /** The tools the plugin added, from its load marker; null when its plugin predates the list. */
    pluginTools: string[] | null;
  }>;
  /**
   * End the publication of the turn `turnId` of a session cubic owns: revoke its grant, then,
   * while the VM runs, write the session's own network again without the forward, so GitHub only
   * reads from the VM. Never resumes a stopped VM.
   */
  endBotTurnPublication: (
    sandboxName: string,
    session: CodeSessionAccessContext,
    turnId: string,
    signal: AbortSignal,
  ) => Promise<void>;
  /**
   * Mint the session's access again and write it to its VM's firewall while the VM runs, so a
   * change to what it brokers, such as its owner's AI subscription disconnected, takes effect now
   * rather than at the VM's next configuration. Never resumes a stopped VM, which is configured
   * afresh when it resumes. It withholds the creator's own MCP accounts, as every configuration
   * outside the session's control lease does; their next turn's brings them back. Resolves whether
   * the VM was running.
   */
  refreshNetwork: (
    sandboxName: string,
    session: CodeSessionAccessContext,
    signal: AbortSignal,
  ) => Promise<boolean>;
  /**
   * A command session on the VM of `sandboxName` while it runs, for reads such as Git's
   * (`readRepositoryState`), and for the controller's writeback from a run's own session
   * (`publishRunFromItsSession`), which also writes files there to rebase. Null when the VM is
   * not running, which this never wakes.
   */
  runningCommandSession: (
    sandboxName: string,
    signal: AbortSignal,
  ) => Promise<CodingAgentRebaseSession | null>;
  /**
   * Run the repository's setup script again in a session's running VM, at its
   * checkout's current commit, as after the user fixed the script. The caller
   * reports the setup row started; this reports its output and outcome. Call
   * `ensureRunning` first to wake the VM. This brokers the repository's
   * current credentials itself: `ensureRunning` can keep a configuration from
   * before the credential the script needs was added. Resolves what the VM
   * got from the run. It withholds the creator's own MCP accounts, which the
   * next turn's configuration brings back when that turn may have them.
   */
  rerunSetup: (input: {
    sessionId: string;
    sandboxName: string;
    repo: CodeSessionAccessContext;
    setup: CodeSessionSetupScript;
    variables: Record<string, string>;
    secretVariableNames: readonly string[];
    onBoot: (progress: CodeBootProgress) => void | Promise<void>;
  }) => Promise<CodeSessionSetupState>;
  /**
   * Fingerprints of every token minted for the sandbox's network policy (its
   * GitHub token and the creator's MCP credentials, withheld ones included),
   * as the last `ensureRunning` or boot through this driver configured or reused
   * it. Used for event redaction; the tokens themselves never leave the driver.
   */
  mintedFingerprints: (sandboxName: string) => CodingAgentCredentialFingerprint[];
  /**
   * What the sandbox's firewall allows, as this driver last configured it,
   * for the agent's `network` instructions entry; null when this driver has
   * not configured it.
   */
  appliedNetwork: (sandboxName: string) => SessionNetworkView | null;
  /**
   * Fingerprints for a transcript read or a turn in any process: the tokens
   * minted for the sandbox and a fork's parent, and the secret values their VMs
   * were given, kept for 90 days after its last configuration: the newest
   * 1,000, so an older one is no longer redacted. The VM's transcript can hold
   * what an MCP server echoed back, or a secret value changed since, long
   * after the process that configured it is gone.
   */
  transcriptFingerprints: (sandboxName: string) => Promise<CodingAgentCredentialFingerprint[]>;
  /**
   * Credentials and values that must not reach session events or transcript
   * reads, beside the minted tokens their fingerprints find: the Gateway key,
   * the repository's own credentials and its secret variables' values
   * (`extraSecrets`), read on each call. Never return them through step
   * outputs: raw values would persist in run state.
   */
  redactionInput: (
    session: CodeSessionAccessContext,
  ) => Promise<Required<Pick<CodeSessionRedactionInput, 'credentials' | 'extraSecrets'>>>;
  /**
   * Non-mutating liveness check: 'running' when the VM is up, 'stopped' when
   * Vercel reports it stopped/failed, 'unknown' when the probe itself failed.
   * Never resumes the sandbox.
   */
  probe: (sandboxName: string) => Promise<'running' | 'stopped' | 'unknown'>;
  /** Read-only discovery: never resumes, reconfigures, or extends the VM lifetime. */
  runningHandle: (sandboxName: string, signal: AbortSignal) => Promise<CodeSandboxHandle | null>;
  stop: (sandboxName: string) => Promise<void>;
  /**
   * Run Git in the session's checkout. A VM that stopped is resumed first,
   * restarting OpenCode as `ensureRunning` does, the model check and
   * `session.creatorAccounts` included.
   */
  runGit: (
    sandboxName: string,
    args: string[],
    session: CodeSessionRuntimeContext,
  ) => Promise<{ exitCode: number; stdout: string; stderr: string }>;
  readWorkspaceFile: (
    sandboxName: string,
    path: string,
    signal: AbortSignal,
  ) => Promise<WorkspaceFileResult>;
  readWorkspaceChanges: (
    sandboxName: string,
    signal: AbortSignal,
  ) => Promise<WorkspaceChangesResult>;
  readWorkspaceDiff: (
    sandboxName: string,
    path: string,
    expectedBase: string,
    signal: AbortSignal,
  ) => Promise<WorkspaceDiffResult>;
  /**
   * The bytes of any file in a running VM, by absolute path, symlinks
   * followed, for a file the agent shares. Never resumes a stopped VM, and
   * reads nothing over `maxBytes`.
   */
  readSandboxFile: (
    sandboxName: string,
    path: string,
    maxBytes: number,
    signal: AbortSignal,
  ) => Promise<SandboxFileRead>;
  client: (handle: CodeSandboxHandle) => OpenCodeClient;
};

export type SandboxFileRead =
  | { kind: 'file'; bytes: Uint8Array }
  | { kind: 'missing' }
  | { kind: 'not-file' }
  | { kind: 'too-large' }
  | { kind: 'unavailable'; reason: 'sandbox-unavailable' | 'error' };
